import { createBackendApiClient, createBackendApiClientWithReq } from "./createBackendApiClient";
import { ServerRequest } from "../../types";
import {
  Competence,
  CompetenceBase,
  CompetenceBaseTree,
  CompetenceBaseWithChildren,
} from "./generated";
import { buildCompetenceUrl } from "@/utils/url";
import { createImageUrl } from "@/server/backend-api/mapHelpers";
import {
  getTopicsWithAllSubtopics,
  sortTopicsBySpecialTopicOrder,
} from "@/utils/competenceTree";

export const rootCompetenceNames = {
  sdg: "SDG/Domain",
  preparatory: "Preparatory",
  transversal: "Transversal",
} as const;

type LegacyRootKey = keyof typeof rootCompetenceNames;

export interface CompetenceBaseEx extends Omit<CompetenceBase, "imagesPath"> {
  imageUri: string | null;
  url: string;
}

export interface CompetenceBaseWithChildrenEx
  extends CompetenceBaseWithChildren {
  imageUri: string | null;
  children: CompetenceBaseWithChildrenEx[];
  url: string;
}

export interface CompetenceEx
  extends Omit<Competence, "imagesPath" | "subTopics"> {
  imageUri: string | null;
  url: string;
  subTopics: CompetenceBaseEx[] | null;
}

export type CompetenceBaseTreeEx = Record<
  string,
  CompetenceBaseWithChildrenEx
>;

export const getCompetences = async (): Promise<CompetenceBaseEx[]> => {
  const client = createBackendApiClient();
  const competences = await client.competence_GetAll();
  return competences.map(mapCompetenceBase);
};

export function mapCompetenceBase(data: CompetenceBase): CompetenceBaseEx {
  return {
    ...data,
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    url: buildCompetenceUrl(data.id, data.name),
  };
}

export const getTopic = async (
  id: string,
  req?: ServerRequest
): Promise<CompetenceEx> => {
  if (req !== undefined && req !== null) {
    const client = createBackendApiClientWithReq(req);
    const data = await client.competence_GetTopicById(id);
    return mapCompetence(data);
  } else {
    const client = createBackendApiClient();
    const data = await client.competence_GetTopicById(id);
    return mapCompetence(data);
  }
};

const mapCompetence = (data: Competence): CompetenceEx => {
  return {
    ...data,
    url: buildCompetenceUrl(data.id, data.name),
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    subTopics: data.subTopics?.map(mapCompetenceBase) ?? null,
  };
};

function normalizeRootKey(name: string | null, id: string): string {
  if (name === null || name.trim().length === 0) {
    return id;
  }

  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function mapCompetenceGetTree(
  tree: CompetenceBaseTree
): CompetenceBaseTreeEx {
  const result: CompetenceBaseTreeEx = {};

  for (const root of tree.rootCompetences) {
    const mapped = mapCompetenceBaseWithChildren(root);
    result[normalizeRootKey(root.name, root.id)] = mapped;
    result[root.id] = mapped;
  }

  for (const [legacyKey, legacyName] of Object.entries(rootCompetenceNames) as [
    LegacyRootKey,
    string
  ][]) {
    const legacyRoot = tree.rootCompetences.find((c) => c.name === legacyName);
    if (legacyRoot) {
      result[legacyKey] = mapCompetenceBaseWithChildren(legacyRoot);
    }
  }

  return result;
}

export async function getCompetenceTree(): Promise<CompetenceBaseTreeEx> {
  const client = createBackendApiClient();
  const tree = await client.competence_GetTree();
  return mapCompetenceGetTree(tree);
}

export function mapCompetenceBaseWithChildrenByName(
  tree: CompetenceBaseTree,
  rootCompetenceName: keyof typeof rootCompetenceNames
): CompetenceBaseWithChildrenEx {
  const rootCompetence = tree.rootCompetences.find(
    (c) => c.name === rootCompetenceNames[rootCompetenceName]
  );
  if (!rootCompetence) {
    throw new Error(`Could not find root competence ${rootCompetenceName}`);
  }
  return mapCompetenceBaseWithChildren(rootCompetence);
}

export function mapCompetenceBaseWithChildren(
  data: CompetenceBaseWithChildren
): CompetenceBaseWithChildrenEx {
  return {
    ...data,
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    children: data.children.map(mapCompetenceBaseWithChildren),
    url: buildCompetenceUrl(data.id, data.name),
  };
}

export async function getSecondLevelCompetences() {
  const tree = await getCompetenceTree();
  const competences = getTopicsWithAllSubtopics(tree);
  return sortTopicsBySpecialTopicOrder(competences);
}
